import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import qs from "qs";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Menu, Dropdown } from "antd";
import classNames from "classnames";
import { Page } from "@app/components/page";
import { AI_SOURCE_TYPE, Review, SearchType } from "@app/constants";
import Details from "@app/components/article/details";
import ArticleSidebar from "@app/components/article/sidebar";
import confirm from "@app/components/confirm";
import ViewToggle, { useArticleViewState } from "@app/components/article/chunks/view-toggle";
import { useSidebar } from "@app/components/page/sidebar";
import ArticleMenu from "@app/components/article/chunks/article-menu";
import ArticleNavigation from "@app/components/article/chunks/navigation";
import { ActionType } from "@app/constants";
import { ReasonType } from "@app/constants";
import OriginalArticleModal from "@app/components/article/original-article-modal";
import Title from "@app/pages/report/page-title";

import l1 from "@app/state/store/report/l1";
import state from "@app/state/store/report/article";
import reportStore from "@app/state/store/report";
import articleActions from "@app/state/store/action/article";
import applicationStore from "@app/state/store/application";
import assigned from "@app/state/store/projects/assigned";
import session from "@app/state/store/session";
import { sanitizeLocationSearch } from "@app/util/sanitize-query-string";
import chatStore from "@app/state/store/ai-chat";

import "./style/article.scoped.scss";

const Article = observer(({ className, ...rest }) => {
    const history = useHistory();
    const [modalOpen, setModalOpen] = useState(false);
    const config = reportStore.config || {};
    const { article, type, project } = useParams();
    const sidebar = useSidebar();
    const viewState = useArticleViewState();
    const reasons = config[type]?.l1.excludeReasons || [];
    const duplicateReason = reasons.find((reason) => ReasonType.DUPLICATE === reason.type);
    const readonly = !session.can("article.update") || reportStore.readOnly;
    const location = useLocation();

    const params = qs.parse(location.search, { ignoreQueryPrefix: true });

    const viewFile = params["view-file"] === "true";

    useEffect(() => {
        if (article) {
            state.load(article);

            articleActions.setContext({ stage: Review.L1, type });
            articleActions.load(ActionType.ARTICLE);

            viewState.reset();

            if (viewFile) {
                viewState.view = "file";
            }
        }
    }, [article, viewState, type, viewFile]);

    useEffect(() => {
        if (!state.article?.file) {
            viewState.view = "summary";
        }

        chatStore.setActiveDocument({
            id: state.article?._id,
            type: AI_SOURCE_TYPE.ARTICLE,
            hasFile: !!state.article?.file,
            name: state.article?.title,
            mdrId: state.article?.mdrId,
        });

        return () => {
            chatStore.setActiveDocument(null);
        };
    }, [state.article]);

    useEffect(() => {
        assigned.load(project);

        return () => {
            chatStore.setActiveDocument(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // do not render unless there is a loaded article
    if (!state.article?._id) {
        return;
    }

    /**
     * Close the article
     */
    const close = () => {
        history.push({ pathname: "../l1", search: history.location.search });
        chatStore.setActiveDocument(null);
    };

    /**
     * Update article details
     */
    const update = async (data) => {
        await state.update(data);
    };

    const navigateTo = (id) => {
        history.push({
            pathname: `../l1/${id}`,
            search: sanitizeLocationSearch(history.location.search),
        });
    };

    const navigateToRecent = (type, stage, id) => {
        history.push({
            pathname: `../../${type}/${stage}/${id}`,
            search: sanitizeLocationSearch(history.location.search),
        });
    };

    /**
     * Perform an pass/fail action on the article
     */
    const resolve = async (action, reason) => {
        // check if we are going to perform any action at all
        if (
            action === "pass" &&
            ((type === SearchType.SLR && state.article.slrL1Passed) ||
                (type === SearchType.SOTA && state.article.sotaL1Passed))
        ) {
            return;
        }

        if (action === "fail" && reason === state.article?.review?.l1?.fail?.reason) {
            return;
        }

        // confirm the action if the l2 review has started
        let l2 = state.article?.review?.l2 || {};
        let started = l2.started || l2.complete;

        if (started) {
            let resume = await confirm(
                "The L2 review process for this article has already started. " +
                    "Your action will discard the L2 progress. Are you sure you want to continue?",
            );

            if (!resume) {
                return;
            }
        }

        if (duplicateReason?.reason === reason) {
            // trigger a original suggestion modal
            setModalOpen(true);
            return;
        }

        if (action === "pass") {
            await state.pass({ stage: Review.L1, type });
        }

        if (action === "fail") {
            await state.fail(Review.L1, { type, reason });
            return;
        }
    };

    /**
     * Toggle the sidebar
     */
    const toggleSidebar = () => {
        sidebar.toggle();
    };

    /**
     * Prepare the reasons menu
     */
    const reasonsMenu = (
        <Menu className="menu">
            {reasons.map((reason) => {
                return (
                    <Menu.Item
                        key={reason._id}
                        onClick={() => {
                            resolve("fail", reason.reason);
                        }}
                    >
                        {reason.reason}
                    </Menu.Item>
                );
            })}
        </Menu>
    );

    const removeFile = async () => {
        // switch to the summary view and remove the file
        viewState.view = "summary";
        await state.removeFile();
    };

    const l1Complete =
        type === SearchType.SLR ? state.article?.slrL1Complete : state.article?.sotaL1Complete;
    const l1Passed =
        type === SearchType.SLR ? state.article?.slrL1Passed : state.article?.sotaL1Passed;

    const includeBtnType = l1Complete && l1Passed ? "success" : "default";
    const excludeBtnType = l1Complete && !l1Passed ? "danger" : "default";

    const next = async () => {
        const id = await l1.getNextArticle();
        navigateTo(id);
    };

    const previous = async () => {
        const id = await l1.getPreviousArticle();
        navigateTo(id);
    };

    const onResetStatus = async () => {
        let proceed = await confirm("Are you sure you want to reset the status?");
        if (proceed) {
            await state.resetReviewStatus({ stage: "l1", type });
        }
    };

    const onRemoveArticle = async () => {
        let proceed = await confirm("Are you sure you want to remove this article?");
        if (proceed) {
            await state.deleteArticle(article);
            close();
        }
    };

    return (
        <Page className={classNames("l1", className)} {...rest}>
            <Page.Header closable={true} onClose={close}>
                <Page.Header.Left>
                    <ArticleNavigation
                        onNext={next}
                        onPrevious={previous}
                        hasNext={l1.hasNextArticle()}
                        hasPrevious={l1.hasPreviousArticle()}
                        recentActions={articleActions?.data}
                        onRecent={navigateToRecent}
                    />
                    <Page.Title breadcrumbs={false}>
                        <Title article={state.article} />
                    </Page.Title>
                </Page.Header.Left>
                <Page.Header.Right>
                    <ViewToggle article={state.article} viewState={viewState} />

                    {!readonly && (
                        <>
                            <Button type={includeBtnType} onClick={() => resolve("pass")}>
                                Include
                            </Button>

                            <Dropdown overlay={reasonsMenu} trigger="click" placement="bottomRight">
                                <Button type={excludeBtnType}>
                                    Exclude <DownOutlined />
                                </Button>
                            </Dropdown>
                        </>
                    )}

                    <ArticleMenu
                        article={state?.article}
                        onUpload={(file) => {
                            update({ file: file._id });
                        }}
                        onRemove={removeFile}
                        onChange={(data) => state.update(data)}
                        onChangeType={(type) => state.addType(type)}
                        switchArticleType={config.sota?.enabled}
                        onResetStatus={onResetStatus}
                        stage="l1"
                        onRemoveArticle={onRemoveArticle}
                        readonly={readonly}
                    />

                    {!sidebar.inline && (
                        <>
                            <Page.Header.Separator />
                            <Button
                                icon={<InfoCircleOutlined />}
                                type="icon"
                                onClick={toggleSidebar}
                            />
                        </>
                    )}
                </Page.Header.Right>
            </Page.Header>

            <Page.Layout>
                <Page.Body className="page-body" padding={false}>
                    <Details
                        article={state.article}
                        type={type}
                        onChange={update}
                        stage={Review.L1}
                        viewState={viewState}
                        keywords={config[type].keywords}
                        onUpload={(file) => {
                            update({ file: file._id });
                        }}
                    />
                    {modalOpen && (
                        <OriginalArticleModal
                            type={type}
                            modalOpen={modalOpen}
                            setModalOpen={setModalOpen}
                            articleIds={[state.article._id]}
                            afterSelect={async () => {
                                await state.fail(Review.L1, {
                                    type,
                                    reason: duplicateReason.reason,
                                });
                            }}
                        />
                    )}
                </Page.Body>

                <ArticleSidebar
                    article={article}
                    state={sidebar}
                    enableAI={applicationStore.enableAI}
                    readonly={reportStore.readOnly}
                    stage={Review.L1}
                />
            </Page.Layout>
        </Page>
    );
});

export default Article;
